import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    createPortal,
} from "react-dom";

import {
    FiAlertCircle,
    FiCheckCircle,
    FiFileText,
    FiLoader,
    FiRefreshCw,
    FiSave,
    FiX,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    createDebitNote,
    confirmDebitNote,
    debitNoteLoadingSelector,
    debitNoteErrorSelector,
} from "../../../../../../../store/billing/debitNotes/debitNoteSlice";

import "../DebitNotes.css";

/*
|--------------------------------------------------------------------------
| REASONS
|--------------------------------------------------------------------------
*/

const DEBIT_NOTE_REASONS = [
    {
        value:
            "PURCHASE_RETURN",

        label:
            "Purchase Return",
    },
    {
        value:
            "DAMAGED_GOODS",

        label:
            "Damaged Goods",
    },
    {
        value:
            "RATE_DIFFERENCE",

        label:
            "Rate Difference",
    },
    {
        value:
            "SHORT_SUPPLY",

        label:
            "Short Supply",
    },
    {
        value:
            "QUALITY_ISSUE",

        label:
            "Quality Issue",
    },
    {
        value:
            "OTHER",

        label:
            "Other",
    },
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const roundQuantity = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
            1000
    ) / 1000;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    ).format(
        date
    );
};

const formatDateInput = (
    value = new Date()
) => {
    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    const year =
        date.getFullYear();

    const month =
        String(
            date.getMonth() +
                1
        ).padStart(
            2,
            "0"
        );

    const day =
        String(
            date.getDate()
        ).padStart(
            2,
            "0"
        );

    return `${year}-${month}-${day}`;
};

/*
|--------------------------------------------------------------------------
| PURCHASE BILL HELPERS
|--------------------------------------------------------------------------
*/

const getPurchaseBillId = (
    purchaseBill
) => {
    if (!purchaseBill) {
        return "";
    }

    return (
        purchaseBill?._id ||
        purchaseBill?.id ||
        purchaseBill
            ?.purchaseBill
            ?._id ||
        purchaseBill
            ?.purchaseBill
            ?.id ||
        purchaseBill
            ?.purchaseBill ||
        ""
    );
};

const getPurchaseBillNumber = (
    purchaseBill
) =>
    purchaseBill
        ?.documentNumber ||
    purchaseBill
        ?.purchaseBillNumber ||
    "-";

const getPurchaseBillAmount = (
    purchaseBill
) =>
    roundMoney(
        purchaseBill
            ?.grandTotal ??
        purchaseBill
            ?.totals
            ?.grandTotal ??
        0
    );

const getPurchaseBillBalance = (
    purchaseBill
) =>
    roundMoney(
        purchaseBill
            ?.balanceAmount ??
        purchaseBill
            ?.totals
            ?.balanceAmount ??
        purchaseBill
            ?.grandTotal ??
        purchaseBill
            ?.totals
            ?.grandTotal ??
        0
    );

/*
|--------------------------------------------------------------------------
| SUPPLIER HELPERS
|--------------------------------------------------------------------------
*/

const getSupplierName = (
    purchaseBill
) =>
    purchaseBill
        ?.supplierSnapshot
        ?.supplierName ||
    purchaseBill
        ?.supplierSnapshot
        ?.companyName ||
    purchaseBill
        ?.supplier
        ?.supplierName ||
    purchaseBill
        ?.supplier
        ?.companyName ||
    "-";

const getSupplierMobile = (
    purchaseBill
) =>
    purchaseBill
        ?.supplierSnapshot
        ?.mobile ||
    purchaseBill
        ?.supplier
        ?.mobile ||
    "-";

/*
|--------------------------------------------------------------------------
| ITEM HELPERS
|--------------------------------------------------------------------------
*/

const getItemId = (
    item
) =>
    item?._id ||
    item?.id ||
    item
        ?.purchaseBillItemId ||
    "";

const getItemName = (
    item
) =>
    item?.itemName ||
    item?.productName ||
    item
        ?.product
        ?.productName ||
    item
        ?.product
        ?.name ||
    "Purchase Item";

const getItemUnit = (
    item
) =>
    item?.unit ||
    "PIECE";

const getPurchasedQuantity = (
    item
) =>
    roundQuantity(
        item?.quantity ??
        item?.qty ??
        item
            ?.purchasedQuantity ??
        0
    );

const getAlreadyReturnedQuantity = (
    item
) =>
    roundQuantity(
        item
            ?.alreadyReturnedQuantity ??
        item
            ?.returnedQuantity ??
        item
            ?.debitNoteReturnedQuantity ??
        item
            ?.returnedQty ??
        0
    );

const getAvailableQuantity = (
    item
) =>
    roundQuantity(
        Math.max(
            getPurchasedQuantity(
                item
            ) -
                getAlreadyReturnedQuantity(
                    item
                ),
            0
        )
    );

const getItemRate = (
    item
) =>
    roundMoney(
        item?.rate ??
        item?.unitPrice ??
        item?.price ??
        0
    );

const getItemTaxRate = (
    item
) => {
    const directRate =
        toNumber(
            item
                ?.taxRate ??
            item
                ?.taxPercentage ??
            item
                ?.gstRate ??
            item
                ?.gstPercent ??
            0
        );

    if (
        directRate >
        0
    ) {
        return directRate;
    }

    const taxableAmount =
        roundMoney(
            item
                ?.taxableAmount ??
            0
        );

    const taxAmount =
        roundMoney(
            item
                ?.taxAmount ??
            0
        );

    if (
        taxableAmount >
            0 &&
        taxAmount >
            0
    ) {
        return roundMoney(
            taxAmount /
                taxableAmount *
                100
        );
    }

    return 0;
};

/*
|--------------------------------------------------------------------------
| CALCULATE RETURN ITEM
|--------------------------------------------------------------------------
|
| We proportionally use the ORIGINAL purchase bill amounts.
|
| This is safer than recalculating from rate only because the purchase
| bill may contain discounts or tax-inclusive prices.
|--------------------------------------------------------------------------
*/

const calculateReturnItem = (
    item,
    requestedQuantity
) => {
    const purchasedQuantity =
        getPurchasedQuantity(
            item
        );

    const availableQuantity =
        getAvailableQuantity(
            item
        );

    const quantity =
        roundQuantity(
            Math.min(
                Math.max(
                    toNumber(
                        requestedQuantity
                    ),
                    0
                ),
                availableQuantity
            )
        );

    const rate =
        getItemRate(
            item
        );

    if (
        quantity <=
            0 ||
        purchasedQuantity <=
            0
    ) {
        return {
            quantity:
                0,

            rate,

            grossAmount:
                0,

            discountAmount:
                0,

            taxableAmount:
                0,

            taxRate:
                getItemTaxRate(
                    item
                ),

            taxAmount:
                0,

            totalAmount:
                0,
        };
    }

    const ratio =
        quantity /
        purchasedQuantity;

    /*
    |--------------------------------------------------------------------------
    | ORIGINAL GROSS
    |--------------------------------------------------------------------------
    */

    const originalGrossAmount =
        roundMoney(
            item
                ?.grossAmount ??
            purchasedQuantity *
                rate
        );

    /*
    |--------------------------------------------------------------------------
    | ORIGINAL DISCOUNT
    |--------------------------------------------------------------------------
    */

    const originalDiscountAmount =
        roundMoney(
            item
                ?.discountAmount ??
            0
        );

    /*
    |--------------------------------------------------------------------------
    | ORIGINAL TAXABLE
    |--------------------------------------------------------------------------
    */

    const originalTaxableAmount =
        roundMoney(
            item
                ?.taxableAmount ??
            Math.max(
                originalGrossAmount -
                    originalDiscountAmount,
                0
            )
        );

    /*
    |--------------------------------------------------------------------------
    | ORIGINAL TAX
    |--------------------------------------------------------------------------
    */

    const taxRate =
        getItemTaxRate(
            item
        );

    const originalTaxAmount =
        roundMoney(
            item
                ?.taxAmount ??
            (
                originalTaxableAmount *
                taxRate /
                100
            )
        );

    /*
    |--------------------------------------------------------------------------
    | ORIGINAL TOTAL
    |--------------------------------------------------------------------------
    */

    const originalTotalAmount =
        roundMoney(
            item
                ?.totalAmount ??
            originalTaxableAmount +
                originalTaxAmount
        );

    /*
    |--------------------------------------------------------------------------
    | PROPORTIONAL RETURN VALUES
    |--------------------------------------------------------------------------
    */

    const grossAmount =
        roundMoney(
            originalGrossAmount *
                ratio
        );

    const discountAmount =
        roundMoney(
            originalDiscountAmount *
                ratio
        );

    const taxableAmount =
        roundMoney(
            originalTaxableAmount *
                ratio
        );

    const taxAmount =
        roundMoney(
            originalTaxAmount *
                ratio
        );

    const totalAmount =
        roundMoney(
            originalTotalAmount *
                ratio
        );

    return {
        quantity,

        rate,

        grossAmount,

        discountAmount,

        taxableAmount,

        taxRate,

        taxAmount,

        totalAmount,
    };
};

/*
|--------------------------------------------------------------------------
| GET ERROR
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
    error,
    fallback
) => {
    if (
        typeof error ===
        "string"
    ) {
        return error;
    }

    return (
        error?.message ||
        error?.payload?.message ||
        error?.payload ||
        error?.response?.data
            ?.message ||
        fallback
    );
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const CreateDebitNoteDialog =
    ({
        open,
        purchaseBill,
        onClose,
        onSuccess,
    }) => {
        const dispatch =
            useDispatch();

        /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

        const loading =
            useSelector(
                debitNoteLoadingSelector
            ) || {};

        const apiError =
            useSelector(
                debitNoteErrorSelector
            ) || {};

        /*
        |--------------------------------------------------------------------------
        | PURCHASE BILL
        |--------------------------------------------------------------------------
        */

        const purchaseBillId =
            getPurchaseBillId(
                purchaseBill
            );

        const purchaseBillItems =
            useMemo(
                () =>
                    Array.isArray(
                        purchaseBill
                            ?.items
                    )
                        ? purchaseBill.items
                        : [],
                [
                    purchaseBill,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | FORM STATE
        |--------------------------------------------------------------------------
        */

        const [
            debitNoteDate,
            setDebitNoteDate,
        ] =
            useState(
                formatDateInput()
            );

        const [
            reason,
            setReason,
        ] =
            useState(
                "PURCHASE_RETURN"
            );

        const [
            reasonDetails,
            setReasonDetails,
        ] =
            useState(
                ""
            );

        const [
            notes,
            setNotes,
        ] =
            useState(
                ""
            );

        /*
        |--------------------------------------------------------------------------
        | SELECTED ITEMS
        |--------------------------------------------------------------------------
        |
        | {
        |   itemId: {
        |       selected: true,
        |       returnQuantity: 1,
        |       reduceStock: true
        |   }
        | }
        |--------------------------------------------------------------------------
        */

        const [
            selectedItems,
            setSelectedItems,
        ] =
            useState(
                {}
            );

        const [
            localError,
            setLocalError,
        ] =
            useState(
                ""
            );

        /*
        |--------------------------------------------------------------------------
        | SUBMITTING
        |--------------------------------------------------------------------------
        */

        const submitting =
            Boolean(
                loading?.create ||
                loading?.confirm
            );

        /*
        |--------------------------------------------------------------------------
        | RESET WHEN OPENED
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {
                if (
                    !open
                ) {
                    return;
                }

                setDebitNoteDate(
                    formatDateInput()
                );

                setReason(
                    "PURCHASE_RETURN"
                );

                setReasonDetails(
                    ""
                );

                setNotes(
                    ""
                );

                setSelectedItems(
                    {}
                );

                setLocalError(
                    ""
                );
            },
            [
                open,
                purchaseBillId,
            ]
        );

        /*
        |--------------------------------------------------------------------------
        | ESCAPE KEY
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {
                if (
                    !open
                ) {
                    return undefined;
                }

                const handleKeyDown =
                    (
                        event
                    ) => {
                        if (
                            event.key ===
                                "Escape" &&
                            !submitting
                        ) {
                            onClose?.();
                        }
                    };

                document.addEventListener(
                    "keydown",
                    handleKeyDown
                );

                /*
                |--------------------------------------------------------------------------
                | PREVENT BACKGROUND SCROLL
                |--------------------------------------------------------------------------
                */

                const previousOverflow =
                    document.body
                        .style
                        .overflow;

                document.body.style.overflow =
                    "hidden";

                return () => {
                    document.removeEventListener(
                        "keydown",
                        handleKeyDown
                    );

                    document.body.style.overflow =
                        previousOverflow;
                };
            },
            [
                open,
                submitting,
                onClose,
            ]
        );

        /*
        |--------------------------------------------------------------------------
        | CALCULATED SELECTED ITEMS
        |--------------------------------------------------------------------------
        */

        const calculatedItems =
            useMemo(
                () => {
                    return purchaseBillItems
                        .map(
                            (
                                item
                            ) => {
                                const itemId =
                                    getItemId(
                                        item
                                    );

                                const selected =
                                    selectedItems[
                                        itemId
                                    ];

                                if (
                                    !selected
                                        ?.selected
                                ) {
                                    return null;
                                }

                                const calculation =
                                    calculateReturnItem(
                                        item,
                                        selected
                                            ?.returnQuantity
                                    );

                                if (
                                    calculation
                                        .quantity <=
                                    0
                                ) {
                                    return null;
                                }

                                return {
                                    item,

                                    itemId,

                                    reduceStock:
                                        selected
                                            ?.reduceStock !==
                                        false,

                                    ...calculation,
                                };
                            }
                        )
                        .filter(
                            Boolean
                        );
                },
                [
                    purchaseBillItems,
                    selectedItems,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | TOTALS
        |--------------------------------------------------------------------------
        */

        const totals =
            useMemo(
                () => {
                    const result =
                        calculatedItems.reduce(
                            (
                                total,
                                current
                            ) => {
                                total.itemCount +=
                                    1;

                                total.returnQuantity +=
                                    current
                                        .quantity;

                                total.grossAmount +=
                                    current
                                        .grossAmount;

                                total.discountAmount +=
                                    current
                                        .discountAmount;

                                total.taxableAmount +=
                                    current
                                        .taxableAmount;

                                total.taxAmount +=
                                    current
                                        .taxAmount;

                                total.debitAmount +=
                                    current
                                        .totalAmount;

                                return total;
                            },
                            {
                                itemCount:
                                    0,

                                returnQuantity:
                                    0,

                                grossAmount:
                                    0,

                                discountAmount:
                                    0,

                                taxableAmount:
                                    0,

                                taxAmount:
                                    0,

                                debitAmount:
                                    0,
                            }
                        );

                    return {
                        itemCount:
                            result
                                .itemCount,

                        returnQuantity:
                            roundQuantity(
                                result
                                    .returnQuantity
                            ),

                        grossAmount:
                            roundMoney(
                                result
                                    .grossAmount
                            ),

                        discountAmount:
                            roundMoney(
                                result
                                    .discountAmount
                            ),

                        taxableAmount:
                            roundMoney(
                                result
                                    .taxableAmount
                            ),

                        taxAmount:
                            roundMoney(
                                result
                                    .taxAmount
                            ),

                        debitAmount:
                            roundMoney(
                                result
                                    .debitAmount
                            ),
                    };
                },
                [
                    calculatedItems,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | SELECT ITEM
        |--------------------------------------------------------------------------
        */

        const handleItemSelection =
            (
                item
            ) => {
                const itemId =
                    getItemId(
                        item
                    );

                if (
                    !itemId
                ) {
                    return;
                }

                const availableQuantity =
                    getAvailableQuantity(
                        item
                    );

                if (
                    availableQuantity <=
                    0
                ) {
                    return;
                }

                setSelectedItems(
                    (
                        previous
                    ) => {
                        const existing =
                            previous[
                                itemId
                            ];

                        const nextSelected =
                            !existing
                                ?.selected;

                        return {
                            ...previous,

                            [itemId]: {
                                selected:
                                    nextSelected,

                                returnQuantity:
                                    nextSelected
                                        ? existing
                                              ?.returnQuantity ||
                                          availableQuantity
                                        : 0,

                                reduceStock:
                                    existing
                                        ?.reduceStock !==
                                    false,
                            },
                        };
                    }
                );

                setLocalError(
                    ""
                );
            };

        /*
        |--------------------------------------------------------------------------
        | QUANTITY CHANGE
        |--------------------------------------------------------------------------
        */

        const handleQuantityChange =
            (
                item,
                value
            ) => {
                const itemId =
                    getItemId(
                        item
                    );

                if (
                    !itemId
                ) {
                    return;
                }

                const maximumQuantity =
                    getAvailableQuantity(
                        item
                    );

                const quantity =
                    roundQuantity(
                        Math.min(
                            Math.max(
                                toNumber(
                                    value
                                ),
                                0
                            ),
                            maximumQuantity
                        )
                    );

                setSelectedItems(
                    (
                        previous
                    ) => ({
                        ...previous,

                        [itemId]: {
                            ...previous[
                                itemId
                            ],

                            selected:
                                quantity >
                                0,

                            returnQuantity:
                                quantity,

                            reduceStock:
                                previous[
                                    itemId
                                ]
                                    ?.reduceStock !==
                                false,
                        },
                    })
                );

                setLocalError(
                    ""
                );
            };

        /*
        |--------------------------------------------------------------------------
        | REDUCE STOCK TOGGLE
        |--------------------------------------------------------------------------
        */

        const handleStockChange =
            (
                item
            ) => {
                const itemId =
                    getItemId(
                        item
                    );

                if (
                    !itemId
                ) {
                    return;
                }

                setSelectedItems(
                    (
                        previous
                    ) => ({
                        ...previous,

                        [itemId]: {
                            ...previous[
                                itemId
                            ],

                            selected:
                                true,

                            returnQuantity:
                                previous[
                                    itemId
                                ]
                                    ?.returnQuantity ||
                                getAvailableQuantity(
                                    item
                                ),

                            reduceStock:
                                !(
                                    previous[
                                        itemId
                                    ]
                                        ?.reduceStock !==
                                    false
                                ),
                        },
                    })
                );
            };

        /*
        |--------------------------------------------------------------------------
        | RETURN ALL
        |--------------------------------------------------------------------------
        */

        const handleReturnAll =
            () => {
                const nextState =
                    {};

                purchaseBillItems.forEach(
                    (
                        item
                    ) => {
                        const itemId =
                            getItemId(
                                item
                            );

                        const available =
                            getAvailableQuantity(
                                item
                            );

                        if (
                            itemId &&
                            available >
                                0
                        ) {
                            nextState[
                                itemId
                            ] = {
                                selected:
                                    true,

                                returnQuantity:
                                    available,

                                reduceStock:
                                    true,
                            };
                        }
                    }
                );

                setSelectedItems(
                    nextState
                );

                setLocalError(
                    ""
                );
            };

        /*
        |--------------------------------------------------------------------------
        | CLEAR
        |--------------------------------------------------------------------------
        */

        const handleClearItems =
            () => {
                setSelectedItems(
                    {}
                );

                setLocalError(
                    ""
                );
            };

        /*
        |--------------------------------------------------------------------------
        | VALIDATION
        |--------------------------------------------------------------------------
        */

        const validateForm =
            () => {
                if (
                    !purchaseBillId
                ) {
                    return "Purchase bill ID is missing.";
                }

                if (
                    !debitNoteDate
                ) {
                    return "Debit note date is required.";
                }

                if (
                    !reason
                ) {
                    return "Debit note reason is required.";
                }

                if (
                    reason ===
                        "OTHER" &&
                    !reasonDetails.trim()
                ) {
                    return "Please enter reason details.";
                }

                if (
                    calculatedItems.length ===
                    0
                ) {
                    return "Please select at least one item to return.";
                }

                const invalidItem =
                    calculatedItems.find(
                        (
                            current
                        ) =>
                            current
                                .quantity <=
                                0 ||
                            current
                                .quantity >
                                getAvailableQuantity(
                                    current
                                        .item
                                )
                    );

                if (
                    invalidItem
                ) {
                    return `Invalid return quantity for ${getItemName(
                        invalidItem.item
                    )}.`;
                }

                if (
                    totals
                        .debitAmount <=
                    0
                ) {
                    return "Debit note amount must be greater than zero.";
                }

                return "";
            };

        /*
        |--------------------------------------------------------------------------
        | CREATE PAYLOAD
        |--------------------------------------------------------------------------
        */

        const buildPayload =
            () => ({
                purchaseBillId,

                debitNoteDate,

                reason,

                reasonDetails:
                    reasonDetails.trim(),

                notes:
                    notes.trim(),

                items:
                    calculatedItems.map(
                        (
                            current
                        ) => ({
                            purchaseBillItemId:
                                current
                                    .itemId,

                            returnQuantity:
                                current
                                    .quantity,

                            /*
                            |--------------------------------------------------------------------------
                            | STOCK
                            |--------------------------------------------------------------------------
                            |
                            | Extra fields are intentionally included so either naming convention
                            | can be consumed by your debit-note service.
                            |--------------------------------------------------------------------------
                            */

                            reduceStock:
                                current
                                    .reduceStock,

                            affectStock:
                                current
                                    .reduceStock,
                        })
                    ),
            });

        /*
        |--------------------------------------------------------------------------
        | SUBMIT
        |--------------------------------------------------------------------------
        */

        const handleSubmit =
            async (
                shouldConfirm =
                    false
            ) => {
                const validationError =
                    validateForm();

                if (
                    validationError
                ) {
                    setLocalError(
                        validationError
                    );

                    return;
                }

                try {
                    setLocalError(
                        ""
                    );

                    /*
                    |--------------------------------------------------------------------------
                    | CREATE DRAFT
                    |--------------------------------------------------------------------------
                    */

                    const createdResult =
                        await dispatch(
                            createDebitNote(
                                buildPayload()
                            )
                        ).unwrap();

                    const createdDebitNote =
                        createdResult
                            ?.data ||
                        createdResult
                            ?.debitNote ||
                        createdResult;

                    let finalDebitNote =
                        createdDebitNote;

                    /*
                    |--------------------------------------------------------------------------
                    | CONFIRM
                    |--------------------------------------------------------------------------
                    */

                    if (
                        shouldConfirm
                    ) {
                        const debitNoteId =
                            createdDebitNote
                                ?._id ||
                            createdDebitNote
                                ?.id;

                        if (
                            !debitNoteId
                        ) {
                            throw new Error(
                                "Debit note was created but its ID was not returned."
                            );
                        }

                        const confirmResult =
                            await dispatch(
                                confirmDebitNote(
                                    debitNoteId
                                )
                            ).unwrap();

                        finalDebitNote =
                            confirmResult
                                ?.data ||
                            confirmResult
                                ?.debitNote ||
                            confirmResult;
                    }

                    /*
                    |--------------------------------------------------------------------------
                    | SUCCESS CALLBACK
                    |--------------------------------------------------------------------------
                    */

                    if (
                        typeof onSuccess ===
                        "function"
                    ) {
                        await onSuccess(
                            finalDebitNote
                        );
                    }

                    onClose?.();
                } catch (
                    error
                ) {
                    setLocalError(
                        getErrorMessage(
                            error,
                            "Unable to create debit note."
                        )
                    );
                }
            };

        /*
        |--------------------------------------------------------------------------
        | API ERROR
        |--------------------------------------------------------------------------
        */

        const reduxError =
            typeof apiError ===
            "string"
                ? apiError
                : apiError
                      ?.create ||
                  apiError
                      ?.confirm ||
                  "";

        /*
        |--------------------------------------------------------------------------
        | CLOSED
        |--------------------------------------------------------------------------
        */

        if (
            !open
        ) {
            return null;
        }

        /*
        |--------------------------------------------------------------------------
        | MODAL CONTENT
        |--------------------------------------------------------------------------
        */

        const modalContent = (
            <div
                className="create-debit-note-overlay"
                role="presentation"
                onMouseDown={(
                    event
                ) => {
                    if (
                        event.target ===
                            event.currentTarget &&
                        !submitting
                    ) {
                        onClose?.();
                    }
                }}
            >
                <div
                    className="create-debit-note-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="create-debit-note-title"
                    onMouseDown={(
                        event
                    ) =>
                        event.stopPropagation()
                    }
                >
                    {/* =====================================================
                        HEADER
                    ===================================================== */}

                    <header className="create-debit-note-header">
                        <div>
                            <small>
                                PURCHASE RETURN
                            </small>

                            <h2 id="create-debit-note-title">
                                Create Debit Note
                            </h2>

                            <p>
                                Return purchased items and reduce supplier payable.
                            </p>
                        </div>

                        <button
                            type="button"
                            disabled={
                                submitting
                            }
                            onClick={() =>
                                onClose?.()
                            }
                            aria-label="Close"
                        >
                            <FiX />
                        </button>
                    </header>

                    {/* =====================================================
                        BODY
                    ===================================================== */}

                    <div className="create-debit-note-body">
                        {/* ERROR */}

                        {(localError ||
                            reduxError) && (
                            <div className="create-debit-note-error">
                                <FiAlertCircle />

                                <span>
                                    {localError ||
                                        reduxError}
                                </span>
                            </div>
                        )}

                        {/* =================================================
                            PURCHASE BILL / SUPPLIER SUMMARY
                        ================================================= */}

                        <div className="create-debit-note-supplier-card">
                            <div>
                                <span>
                                    Purchase Bill
                                </span>

                                <strong>
                                    {getPurchaseBillNumber(
                                        purchaseBill
                                    )}
                                </strong>

                                <small>
                                    {formatDate(
                                        purchaseBill
                                            ?.documentDate
                                    )}
                                </small>
                            </div>

                            <div>
                                <span>
                                    Supplier
                                </span>

                                <strong>
                                    {getSupplierName(
                                        purchaseBill
                                    )}
                                </strong>

                                <small>
                                    {getSupplierMobile(
                                        purchaseBill
                                    )}
                                </small>
                            </div>

                            <div>
                                <span>
                                    Original Bill
                                </span>

                                <strong>
                                    {formatCurrency(
                                        getPurchaseBillAmount(
                                            purchaseBill
                                        )
                                    )}
                                </strong>

                                <small>
                                    Purchase value
                                </small>
                            </div>

                            <div className="create-debit-note-credit-card">
                                <span>
                                    Current Balance
                                </span>

                                <strong>
                                    {formatCurrency(
                                        getPurchaseBillBalance(
                                            purchaseBill
                                        )
                                    )}
                                </strong>

                                <small>
                                    Supplier payable
                                </small>
                            </div>
                        </div>

                        {/* =================================================
                            FORM DETAILS
                        ================================================= */}

                        <div className="create-debit-note-form-grid">
                            <div>
                                <label>
                                    Debit Note Date *
                                </label>

                                <input
                                    type="date"
                                    value={
                                        debitNoteDate
                                    }
                                    disabled={
                                        submitting
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setDebitNoteDate(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div>
                                <label>
                                    Reason *
                                </label>

                                <select
                                    value={
                                        reason
                                    }
                                    disabled={
                                        submitting
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setReason(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                >
                                    {DEBIT_NOTE_REASONS.map(
                                        (
                                            option
                                        ) => (
                                            <option
                                                key={
                                                    option.value
                                                }
                                                value={
                                                    option.value
                                                }
                                            >
                                                {
                                                    option.label
                                                }
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>

                            <div
                                style={{
                                    gridColumn:
                                        "1 / -1",
                                }}
                            >
                                <label>
                                    Reason Details
                                </label>

                                <input
                                    type="text"
                                    maxLength="500"
                                    value={
                                        reasonDetails
                                    }
                                    disabled={
                                        submitting
                                    }
                                    placeholder="Optional reason details..."
                                    onChange={(
                                        event
                                    ) =>
                                        setReasonDetails(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>
                        </div>

                        {/* =================================================
                            RETURN ITEMS HEADER
                        ================================================= */}

                        <div className="create-debit-note-items-header">
                            <div>
                                <h3>
                                    Return Items
                                </h3>

                                <p>
                                    Select purchased items and enter the quantity being returned to the supplier.
                                </p>
                            </div>

                            <div
                                style={{
                                    display:
                                        "flex",

                                    gap:
                                        "8px",
                                }}
                            >
                                <button
                                    type="button"
                                    className="debit-note-button"
                                    disabled={
                                        submitting ||
                                        !purchaseBillItems
                                            .length
                                    }
                                    onClick={
                                        handleReturnAll
                                    }
                                >
                                    <FiRefreshCw />

                                    Return All
                                </button>

                                <button
                                    type="button"
                                    className="debit-note-button"
                                    disabled={
                                        submitting
                                    }
                                    onClick={
                                        handleClearItems
                                    }
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        {/* =================================================
                            ITEMS TABLE
                        ================================================= */}

                        <div className="create-debit-note-table-wrapper">
                            <table className="create-debit-note-table">
                                <thead>
                                    <tr>
                                        <th>
                                            Select
                                        </th>

                                        <th>
                                            Item
                                        </th>

                                        <th>
                                            HSN
                                        </th>

                                        <th className="text-right">
                                            Purchased
                                        </th>

                                        <th className="text-right">
                                            Returned
                                        </th>

                                        <th className="text-right">
                                            Available
                                        </th>

                                        <th className="text-right">
                                            Return Qty
                                        </th>

                                        <th className="text-right">
                                            Rate
                                        </th>

                                        <th className="text-right">
                                            GST
                                        </th>

                                        <th className="text-right">
                                            Amount
                                        </th>

                                        <th>
                                            Stock
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {purchaseBillItems.map(
                                        (
                                            item,
                                            index
                                        ) => {
                                            const itemId =
                                                getItemId(
                                                    item
                                                );

                                            const selected =
                                                selectedItems[
                                                    itemId
                                                ];

                                            const purchasedQuantity =
                                                getPurchasedQuantity(
                                                    item
                                                );

                                            const alreadyReturnedQuantity =
                                                getAlreadyReturnedQuantity(
                                                    item
                                                );

                                            const availableQuantity =
                                                getAvailableQuantity(
                                                    item
                                                );

                                            const calculation =
                                                calculateReturnItem(
                                                    item,
                                                    selected
                                                        ?.returnQuantity ||
                                                        0
                                                );

                                            const disabledItem =
                                                availableQuantity <=
                                                0;

                                            return (
                                                <tr
                                                    key={
                                                        itemId ||
                                                        index
                                                    }
                                                    className={
                                                        selected
                                                            ?.selected
                                                            ? "is-selected"
                                                            : ""
                                                    }
                                                >
                                                    {/* SELECT */}

                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={
                                                                Boolean(
                                                                    selected
                                                                        ?.selected
                                                                )
                                                            }
                                                            disabled={
                                                                submitting ||
                                                                disabledItem
                                                            }
                                                            onChange={() =>
                                                                handleItemSelection(
                                                                    item
                                                                )
                                                            }
                                                        />
                                                    </td>

                                                    {/* ITEM */}

                                                    <td>
                                                        <div className="create-debit-note-item">
                                                            <strong>
                                                                {getItemName(
                                                                    item
                                                                )}
                                                            </strong>

                                                            <span>
                                                                {getItemUnit(
                                                                    item
                                                                )}
                                                            </span>
                                                        </div>
                                                    </td>

                                                    {/* HSN */}

                                                    <td>
                                                        {item
                                                            ?.hsnSac ||
                                                            item
                                                                ?.hsnSacCode ||
                                                            "-"}
                                                    </td>

                                                    {/* PURCHASED */}

                                                    <td className="text-right">
                                                        {
                                                            purchasedQuantity
                                                        }
                                                    </td>

                                                    {/* ALREADY RETURNED */}

                                                    <td className="text-right">
                                                        {
                                                            alreadyReturnedQuantity
                                                        }
                                                    </td>

                                                    {/* AVAILABLE */}

                                                    <td className="text-right">
                                                        <strong>
                                                            {
                                                                availableQuantity
                                                            }
                                                        </strong>
                                                    </td>

                                                    {/* RETURN QTY */}

                                                    <td className="text-right">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max={
                                                                availableQuantity
                                                            }
                                                            step="0.001"
                                                            disabled={
                                                                submitting ||
                                                                !selected
                                                                    ?.selected ||
                                                                disabledItem
                                                            }
                                                            value={
                                                                selected
                                                                    ?.selected
                                                                    ? selected
                                                                          ?.returnQuantity ??
                                                                      ""
                                                                    : ""
                                                            }
                                                            onChange={(
                                                                event
                                                            ) =>
                                                                handleQuantityChange(
                                                                    item,
                                                                    event
                                                                        .target
                                                                        .value
                                                                )
                                                            }
                                                        />
                                                    </td>

                                                    {/* RATE */}

                                                    <td className="text-right">
                                                        {formatCurrency(
                                                            getItemRate(
                                                                item
                                                            )
                                                        )}
                                                    </td>

                                                    {/* GST */}

                                                    <td className="text-right">
                                                        {getItemTaxRate(
                                                            item
                                                        )}
                                                        %
                                                    </td>

                                                    {/* AMOUNT */}

                                                    <td className="text-right">
                                                        <strong className="create-debit-note-amount">
                                                            {formatCurrency(
                                                                calculation
                                                                    .totalAmount
                                                            )}
                                                        </strong>
                                                    </td>

                                                    {/* STOCK */}

                                                    <td>
                                                        <label className="create-debit-note-stock-toggle">
                                                            <input
                                                                type="checkbox"
                                                                checked={
                                                                    selected
                                                                        ?.selected
                                                                        ? selected
                                                                              ?.reduceStock !==
                                                                          false
                                                                        : false
                                                                }
                                                                disabled={
                                                                    submitting ||
                                                                    !selected
                                                                        ?.selected
                                                                }
                                                                onChange={() =>
                                                                    handleStockChange(
                                                                        item
                                                                    )
                                                                }
                                                            />

                                                            <span>
                                                                Reduce
                                                            </span>
                                                        </label>
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    )}

                                    {!purchaseBillItems.length && (
                                        <tr>
                                            <td
                                                colSpan="11"
                                                className="create-debit-note-empty"
                                            >
                                                <FiFileText />

                                                <h3>
                                                    No purchase items available
                                                </h3>

                                                <p>
                                                    This purchase bill does not contain returnable items.
                                                </p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* =================================================
                            SUMMARY
                        ================================================= */}

                        <div className="create-debit-note-summary">
                            <div>
                                <span>
                                    Return Items
                                </span>

                                <strong>
                                    {
                                        totals.itemCount
                                    }
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Return Quantity
                                </span>

                                <strong>
                                    {
                                        totals.returnQuantity
                                    }
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Tax Amount
                                </span>

                                <strong>
                                    {formatCurrency(
                                        totals
                                            .taxAmount
                                    )}
                                </strong>
                            </div>

                            <div className="create-debit-note-summary-total">
                                <span>
                                    Debit Note Amount
                                </span>

                                <strong>
                                    {formatCurrency(
                                        totals
                                            .debitAmount
                                    )}
                                </strong>
                            </div>
                        </div>

                        {/* =================================================
                            NOTES
                        ================================================= */}

                        <div className="create-debit-note-notes">
                            <label>
                                Notes
                            </label>

                            <textarea
                                rows="3"
                                maxLength="1000"
                                value={
                                    notes
                                }
                                disabled={
                                    submitting
                                }
                                placeholder="Optional debit note notes..."
                                onChange={(
                                    event
                                ) =>
                                    setNotes(
                                        event
                                            .target
                                            .value
                                    )
                                }
                            />
                        </div>
                    </div>

                    {/* =====================================================
                        FOOTER
                    ===================================================== */}

                    <footer className="create-debit-note-footer">
                        <button
                            type="button"
                            disabled={
                                submitting
                            }
                            onClick={() =>
                                onClose?.()
                            }
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="create-debit-note-draft-button"
                            disabled={
                                submitting ||
                                totals
                                    .debitAmount <=
                                    0
                            }
                            onClick={() =>
                                handleSubmit(
                                    false
                                )
                            }
                        >
                            {submitting ? (
                                <FiLoader className="create-debit-note-spinner" />
                            ) : (
                                <FiSave />
                            )}

                            Save Draft
                        </button>

                        <button
                            type="button"
                            className="create-debit-note-confirm-button"
                            disabled={
                                submitting ||
                                totals
                                    .debitAmount <=
                                    0
                            }
                            onClick={() =>
                                handleSubmit(
                                    true
                                )
                            }
                        >
                            {submitting ? (
                                <FiLoader className="create-debit-note-spinner" />
                            ) : (
                                <FiCheckCircle />
                            )}

                            Save & Confirm
                        </button>
                    </footer>
                </div>
            </div>
        );

        /*
        |--------------------------------------------------------------------------
        | PORTAL
        |--------------------------------------------------------------------------
        |
        | This prevents the modal from being rendered inside PurchaseBillView
        | containers that may have overflow/transform/layout rules.
        |--------------------------------------------------------------------------
        */

        if (
            typeof document ===
            "undefined"
        ) {
            return modalContent;
        }

        return createPortal(
            modalContent,
            document.body
        );
    };

export {
    DEBIT_NOTE_REASONS,
    calculateReturnItem,
    getAvailableQuantity,
    getAlreadyReturnedQuantity,
};

export default
    CreateDebitNoteDialog;